import { gpSystemTemplatesKeys } from 'consts/gpConsts'
import { createSelector } from 'reselect'
import i18n from 'resources/locales/i18n'
import { isUserOfAnyAdminRole } from 'utils/authUtils'
import { isGPPlan } from 'utils/billingUtils'
import { logInfo } from 'utils/logUtils'

// OPTIMIZED: Use granular selectors to prevent unnecessary re-renders
// Previously used entire state objects which caused re-renders on any state change
const selectDoctorTags = state => state.profileReducer.doctor?.tags?.items
const selectPatientTags = state => state.patientsReducer.patient?.patientTags?.items
const selectPatientId = state => state.patientsReducer.patient?.id
const selectUserId = state => state.profileReducer.doctor?.user?.id
const selectDoctorTagsSearchTerm = state => state.profileReducer.doctor?.tags?.searchTerm

export const selectPickableTags = createSelector(
  [selectDoctorTags, selectPatientTags, selectPatientId, selectUserId, selectDoctorTagsSearchTerm],
  (doctorTagsRaw, patientTagsRaw, patientId, userId, searchTerm) => {
    const startTime = performance.now()
    const isAdmin = isUserOfAnyAdminRole()

    // For admin users, doctor.tags.items is populated by mpFetchCustomTagsByDoctorEpic which
    // fetches all pages of tagsByDoctorId upfront. Search is done client-side via searchTerm.
    // For non-admin users, doctor.tags.items is updated by backend search (searchDoctorTagsEpic).
    let doctorTags = []
    if (isAdmin) {
      const baseTags = (doctorTagsRaw || []).filter(tag => !tag._deleted && !tag.isDeleted)
      const normalizedSearch = (searchTerm || '').toLowerCase()
      doctorTags = normalizedSearch
        ? baseTags.filter(tag => (tag.value || '').toLowerCase().includes(normalizedSearch))
        : baseTags
    } else {
      doctorTags = doctorTagsRaw?.filter(tag => !tag._deleted && !tag.isDeleted) || []
    }

    // Filter valid patient tags
    const patientTags = patientTagsRaw?.filter(patientTag => !patientTag._deleted && patientTag.tag) || []

    // Create set of assigned tag values for O(1) lookup
    const patientTagValues = new Set(patientTags.map(pt => (pt?.tag?.value || '').toString().toLowerCase()))

    // Filter out already assigned tags and non-pickable tag types (e.g. assignee tags come
    // from tagsByDoctorId for admin users but should never appear in the picker)
    const pickableTags = doctorTags.filter(
      tag => tag.type !== 'assignee' && !patientTagValues.has((tag?.value || '').toString().toLowerCase())
    )

    const duration = performance.now() - startTime
    if (duration > 3) {
      logInfo('[selectPickableTags] PERF: Slow selector execution', {
        component: 'profileSelector',
        event: 'select_pickable_tags_slow',
        durationMs: duration,
        doctorTagsCount: doctorTags?.length,
        patientTagsCount: patientTags?.length,
        userId
      })
    }

    return pickableTags
  }
)

export const selectCustomTemplates = state =>
  state.profileReducer.customTemplates.data?.sort((a, b) => a.title.localeCompare(b.title))

export const selectSystemTemplates = state =>
  state.profileReducer.systemTemplates?.data
    ?.filter(template =>
      isGPPlan(state.practiceReducer.billing.grinPlanKey)
        ? gpSystemTemplatesKeys.some(gpTemplateKey => template.title.includes(gpTemplateKey))
        : true
    )
    ?.map(template => ({ ...template, title: i18n.t(template.title), text: i18n.t(template.text) }))
    .sort((a, b) => a.title.localeCompare(b.title))
